import React, { useEffect, useState } from 'react';
import { Target, Trash2, CheckCircle2, XCircle, Clock, Sparkles, Pencil, Save, X } from 'lucide-react';

interface Oracle {
    id: string;
    name: string;
    description: string;
    oracle_type: string;
    content?: string;
    category?: string;
    created_at?: string;
}

interface Suggestion {
    match_reason: string;
    template: Oracle;
    is_new_detection?: boolean;
}

interface AssignedOracle extends Oracle {
    oracle_id: string;          // The Oracle's own ID (used for PATCH)
    oracle_name: string;        // Alias returned by API (backward compat)
    validation_status: string | null;
    validation_result: any;
    last_validated_at: string | null;
}

interface EditState {
    name: string;
    description: string;
    oracle_type: string;
    content: string;
}

interface OraclesTabProps {
    entityId: string;
    projectId: string;
}

const OraclesTab: React.FC<OraclesTabProps> = ({ entityId, projectId }) => {
    const [assigned, setAssigned] = useState<AssignedOracle[]>([]);
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
    const [isApplying, setIsApplying] = useState(false);
    const [editingOracleId, setEditingOracleId] = useState<string | null>(null); // oracle.oracle_id for PATCH
    const [editingId, setEditingId] = useState<string | null>(null);             // entity_oracle.id for UI key
    const [editState, setEditState] = useState<EditState>({ name: '', description: '', oracle_type: '', content: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        loadOracles();
    }, [entityId, projectId]);

    const loadOracles = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(
                `/api/spec-review/projects/${projectId}/entities/${entityId}/oracles`,
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setAssigned(data.assigned || []);
            }
        } catch (error) {
            console.error('Error loading oracles:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadSuggestions = async () => {
        try {
            const response = await fetch(
                `/api/oracles/suggestions/${entityId}?project_id=${projectId}`,
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }
            );
            if (response.ok) {
                const data = await response.json();
                setSuggestions(data);
                setShowSuggestions(true);
            }
        } catch (error) {
            console.error('Error loading suggestions:', error);
        }
    };

    const handleApplyTemplates = async () => {
        if (selectedTemplates.size === 0) return;

        try {
            setIsApplying(true);

            const draftsToCreate: any[] = [];
            const templateIdsToApply: string[] = [];

            suggestions.forEach(s => {
                if (selectedTemplates.has(s.template.id)) {
                    if (s.is_new_detection) {
                        draftsToCreate.push(s.template);
                    } else {
                        templateIdsToApply.push(s.template.id);
                    }
                }
            });

            const response = await fetch(
                `/api/oracles/${entityId}/apply-templates?project_id=${projectId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        template_ids: templateIdsToApply,
                        drafts: draftsToCreate
                    })
                }
            );

            if (response.ok) {
                await loadOracles();
                setShowSuggestions(false);
                setSelectedTemplates(new Set());
            }
        } catch (error) {
            console.error('Error applying templates:', error);
        } finally {
            setIsApplying(false);
        }
    };

    const toggleTemplateSelection = (id: string) => {
        const newSet = new Set(selectedTemplates);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedTemplates(newSet);
    };

    const handleRemove = async (oracleId: string) => {
        if (!confirm('Are you sure you want to remove this oracle?')) return;
        try {
            const response = await fetch(
                `/api/spec-review/projects/${projectId}/entities/${entityId}/oracles/${oracleId}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }
            );
            if (response.ok) await loadOracles();
        } catch (error) {
            console.error('Error removing oracle:', error);
        }
    };

    const handleStartEdit = (oracle: AssignedOracle) => {
        setEditingId(oracle.id);                          // EntityOracle ID (for card key)
        setEditingOracleId(oracle.oracle_id || oracle.id); // Oracle ID (for PATCH)
        setEditState({
            name: oracle.name || oracle.oracle_name || '',
            description: oracle.description || '',
            oracle_type: oracle.oracle_type,
            content: oracle.content || ''
        });
        setSaveError(null);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingOracleId(null);
        setSaveError(null);
    };

    const handleSaveEdit = async () => {
        if (!editingOracleId) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            const response = await fetch(
                `/api/oracles/${editingOracleId}`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(editState)
                }
            );
            if (response.ok) {
                setEditingId(null);
                await loadOracles();
            } else {
                const err = await response.json().catch(() => ({}));
                setSaveError(err.detail || 'Failed to save oracle.');
            }
        } catch (error) {
            setSaveError('Network error. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const getStatusIcon = (status: string | null) => {
        switch (status) {
            case 'PASSED': return <CheckCircle2 className="w-4 h-4 text-green-400" />;
            case 'FAILED': return <XCircle className="w-4 h-4 text-red-400" />;
            case 'PENDING': return <Clock className="w-4 h-4 text-yellow-400" />;
            default: return <Clock className="w-4 h-4 text-slate-400" />;
        }
    };

    const ORACLE_TYPES = ['BUSINESS_RULE', 'SECURITY', 'PERFORMANCE', 'UI_BEHAVIOR', 'MOCKING_RULE', 'API_CONTRACT', 'DATA_VALIDATION', 'UI_FORMAT'];

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h4 className="text-md font-semibold text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    Assigned Oracles ({assigned.length})
                </h4>
                <button
                    onClick={loadSuggestions}
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 text-primary hover:bg-primary/30 rounded-lg text-sm font-medium transition-colors"
                >
                    <Sparkles className="w-4 h-4" />
                    Suggest Oracles
                </button>
            </div>

            {/* Suggestions Panel */}
            {showSuggestions && (
                <div className="bg-slate-800/80 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h5 className="font-medium text-white flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-yellow-400" />
                            AI Suggestions
                        </h5>
                        <button
                            onClick={() => setShowSuggestions(false)}
                            className="text-slate-400 hover:text-white"
                        >
                            Close
                        </button>
                    </div>

                    {suggestions.length === 0 ? (
                        <p className="text-slate-400 text-sm">No relevant oracles found for this entity.</p>
                    ) : (
                        <div className="space-y-2">
                            {suggestions.map((s) => (
                                <div
                                    key={s.template.id}
                                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedTemplates.has(s.template.id)
                                        ? s.is_new_detection ? 'bg-yellow-500/10 border-yellow-500' : 'bg-primary/20 border-primary'
                                        : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                                        }`}
                                    onClick={() => toggleTemplateSelection(s.template.id)}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-4 h-4 rounded border mt-1 flex items-center justify-center ${selectedTemplates.has(s.template.id)
                                            ? s.is_new_detection ? 'bg-yellow-500 border-yellow-500' : 'bg-primary border-primary'
                                            : 'border-slate-500'
                                            }`}>
                                            {selectedTemplates.has(s.template.id) && <CheckCircle2 className="w-3 h-3 text-white" />}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-white text-sm">{s.template.name}</p>
                                                {s.is_new_detection && (
                                                    <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] rounded border border-yellow-500/30 uppercase font-bold tracking-wider">
                                                        Detected
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-400 mb-1">{s.template.description}</p>
                                            {s.is_new_detection && (s.template as any).evidence_snippet && (
                                                <div className="mt-1 p-2 bg-slate-950 rounded border border-slate-800 font-mono text-[10px] text-slate-300">
                                                    "...{(s.template as any).evidence_snippet}..."
                                                </div>
                                            )}
                                            <p className="text-xs text-blue-400 italic mt-1">Match: {s.match_reason}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={handleApplyTemplates}
                                    disabled={selectedTemplates.size === 0 || isApplying}
                                    className="px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50"
                                >
                                    {isApplying ? 'Applying...' : `Apply Selected (${selectedTemplates.size})`}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Assigned Oracle List */}
            {assigned.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-slate-800 rounded-lg">
                    <p className="text-slate-500 text-sm">No oracles assigned yet. Use suggestions to start!</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {assigned.map((oracle) => (
                        <div key={oracle.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                            {editingId === oracle.id ? (
                                /* ── Inline Edit Form ── */
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">Name</label>
                                            <input
                                                type="text"
                                                value={editState.name}
                                                onChange={e => setEditState(s => ({ ...s, name: e.target.value }))}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-400 mb-1">Type</label>
                                            <select
                                                value={editState.oracle_type}
                                                onChange={e => setEditState(s => ({ ...s, oracle_type: e.target.value }))}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
                                            >
                                                {ORACLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Description</label>
                                        <input
                                            type="text"
                                            value={editState.description}
                                            onChange={e => setEditState(s => ({ ...s, description: e.target.value }))}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 mb-1">Content</label>
                                        <textarea
                                            value={editState.content}
                                            onChange={e => setEditState(s => ({ ...s, content: e.target.value }))}
                                            rows={4}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary font-mono resize-y"
                                            placeholder="Oracle content / instruction for AI test generation..."
                                        />
                                    </div>
                                    {saveError && (
                                        <p className="text-xs text-red-400">{saveError}</p>
                                    )}
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={handleCancelEdit}
                                            disabled={isSaving}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 rounded-lg transition-colors"
                                        >
                                            <X className="w-3.5 h-3.5" /> Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveEdit}
                                            disabled={isSaving}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary/90 hover:bg-primary text-white rounded-lg transition-colors disabled:opacity-50"
                                        >
                                            <Save className="w-3.5 h-3.5" />
                                            {isSaving ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* ── Read-only Card ── */
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-semibold text-white">{oracle.name}</span>
                                            <span className="text-xs px-2 py-0.5 bg-slate-700 rounded text-slate-300">
                                                {oracle.oracle_type}
                                            </span>
                                            {getStatusIcon(oracle.validation_status)}
                                        </div>
                                        <p className="text-sm text-slate-400">{oracle.description}</p>
                                        {oracle.content && (
                                            <p className="mt-2 text-xs text-slate-500 font-mono bg-slate-900/60 rounded px-2 py-1.5 border border-slate-800 line-clamp-2">
                                                {oracle.content}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 ml-3 shrink-0">
                                        <button
                                            onClick={() => handleStartEdit(oracle)}
                                            title="Edit oracle"
                                            className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-blue-400 transition-colors"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleRemove(oracle.id)}
                                            title="Remove oracle"
                                            className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-red-400 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default OraclesTab;
